import cx from 'classnames';
import {useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {Icon, InformationModal} from 'components';
import {IdAnalyzerFaceVerificationData, ParticipantAsyncRequest, AsyncRequestStatusName} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';

const T_KEYS = TRANSLATION_KEYS.IN_SESSION_PAGE.ID_ANALYZER_FACE_VERIFICATION_MODAL;

type Props = {
    open: boolean;
    asyncRequest: ParticipantAsyncRequest<IdAnalyzerFaceVerificationData>;
    onClose: () => void;
};

export const IdAnalyzerFaceVerificationModal: React.FC<Props> = ({open, asyncRequest, onClose}: Props) => {
    const {t} = useTranslation();
    const {success, failed} = useMemo(() => {
        const successStatus = asyncRequest?.status.name === AsyncRequestStatusName.Success;
        return {success: successStatus, failed: !successStatus};
    }, [asyncRequest]);
    const data = useMemo(() => asyncRequest?.data, [asyncRequest]);

    const renderVerificationErrorDetails = () => {
        const response = data?.response;
        if (!response) {
            return null;
        }
        const data_result = response?.result;
        const authentication_result = response?.authentication;
        const face_result = response?.face;
        if (!data_result) {
            return null;
        }

        // Print result
        const result = [];
        const name = data_result?.firstName || data_result?.lastName ?
            `${data_result?.firstName} ${data_result?.lastName}` : t(T_KEYS.N_A);
        result.push(`${t(T_KEYS.NAME_IS)} ${name}`);

        // Parse document authentication results
        if (authentication_result) {
            if (authentication_result.score > 0.5) {
                result.push(t(T_KEYS.DOC_AUTHENTIC));
            } else if (authentication_result.score > 0.3) {
                result.push(t(T_KEYS.DOC_SUSPICIOUS));
            } else {
                result.push(t(T_KEYS.DOC_FAKE));
            }
        }
        // Parse biometric verification results
        if (face_result) {
            if (face_result.isIdentical) {
                result.push(t(T_KEYS.VERIFICATION_PASSED));
            } else {
                result.push(t(T_KEYS.VERIFICATION_FAILED));
            }
            result.push(`${t(T_KEYS.CONFIDENCE_SCORE)}: ${face_result.confidence || t(T_KEYS.N_A)}`);
        }

        return result.map((res, i) => <div key={i} className='tw-my-1'>{res}</div>);
    };

    return (
        <InformationModal
            open={open}
            closeTimeoutMS={0}
            fullWidth
            maxWidth='sm'
            onClose={onClose}
        >
            <div className='tw-flex tw-flex-col tw-items-center'>
                {success ?
                    <Icon name='check-circle' color='success' size='8xl' thickness='sm' className='tw-self-center' /> :
                    <Icon name='x-circle' color='alert' size='8xl' thickness='sm' className='tw-self-center' />}
                <div className='tw-inline-block'>
                    <div className={cx('tw-mt-6 tw-ts-h2', {'tw-text-success': success, 'tw-text-alert': failed})}>
                        {t(success ? T_KEYS.TITLE_SUCCESS : T_KEYS.TITLE_FAILED)}
                    </div>
                    {failed && data?.error &&
                        <div className='tw-my-4'>
                            {data?.error?.message || t(T_KEYS.UNEXPECTED_ERROR)}
                        </div>}
                    {data && !data?.error &&
                        <div className='tw-my-4'>
                            {renderVerificationErrorDetails()}
                        </div>}
                </div>
            </div>
        </InformationModal>
    );
};
